"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState, ReactNode } from "react";

interface ReactQueryProviderProps {
  readonly children: ReactNode;
}

export default function ReactQueryProvider({
  children,
}: ReactQueryProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Stale time: 5 minutes - data is considered fresh for 5 minutes
            staleTime: 5 * 60 * 1000,

            // Cache time: 10 minutes - data stays in cache for 10 minutes when unused
            gcTime: 10 * 60 * 1000,

            // Refetch on window focus for real-time updates
            refetchOnWindowFocus: true,

            // Retry failed requests once. A 3x global retry combined with the
            // exponential backoff below stacks 4 in-flight requests per query
            // when the backend hiccups, which exhausts the browser's per-origin
            // connection budget (6 on HTTP/1.1) and freezes client navigation.
            // Mutations and critical queries can override locally.
            retry: 1,

            // Exponential backoff for retries
            retryDelay: (attemptIndex) =>
              Math.min(1000 * 2 ** attemptIndex, 30000),

            // Refetch on network reconnect
            refetchOnReconnect: true,

            // NOTE: no global `refetchInterval`. A global default polls *every*
            // active query in the app every N seconds — health checks, configs,
            // drafts, conversation lists — even though almost none of them
            // need polling. Combined with a long-running SSE stream on the
            // same origin (playground turn), the pile-up exhausts the 6
            // connections/origin HTTP/1.1 cap and stalls client navigation.
            // Hooks that genuinely need polling (e.g. `useAgentServiceHealth`,
            // `useKnowledgeBaseQuery` during ingest) set `refetchInterval`
            // locally.
          },
          mutations: {
            // Retry mutations once on failure
            retry: 1,

            // Optimistic updates enabled by default
            onError: (error, variables, context) => {
              console.error("Mutation failed:", error);
            },
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV !== "production" && (
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
}
